import { Component, OnInit, OnDestroy } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { Location } from '@angular/common';
import { Subject, takeUntil } from 'rxjs';
import { VehicleRentalService } from '../../../../services/vehicle-rental.service';

// Interfaces locais para evitar problemas de importação
interface VehicleRental {
  id: number;
  rentalNumber: string;
  startDate: string;
  endDate: string;
  priority: string;
  status: string;
  customerId?: number;
  customerName?: string;
  driverName?: string;
  notes?: string;
}

interface VehicleRentalItem {
  id: number;
  vehicleId: number;
  plateNumber: string;
  brand: string;
  model: string;
  startMileage: number;
  endMileage?: number;
  conditionAtStart?: string;
  status: string;
}

@Component({
  selector: 'app-vehicle-rental-details',
  templateUrl: './vehicle-rental-details.component.html',
  styleUrls: ['./vehicle-rental-details.component.css']
})
export class VehicleRentalDetailsComponent implements OnInit, OnDestroy {
  private destroy$ = new Subject<void>();

  // Propriedades
  rentalId: number | null = null;
  error: string | null = null;
  vehicleRental: VehicleRental | null = null;
  rentalItems: VehicleRentalItem[] = [];
  isLoading = true;
  activeTab: 'overview' | 'items' | 'history' | 'attachments' = 'overview';

  // Dados do histórico
  history: any[] = [];
  isLoadingHistory = false;

  // Dados dos anexos
  attachments: any[] = [];
  isLoadingAttachments = false;

  // Dados mock para demonstração
  mockRental: VehicleRental = {
    id: 1,
    rentalNumber: 'LOC-20240918-001',
    startDate: '2024-09-20',
    endDate: '2024-09-25',
    priority: 'NORMAL',
    status: 'PENDENTE',
    customerId: 1,
    customerName: 'João Silva',
    driverName: 'João Silva',
    notes: 'Locação para viagem de negócios'
  };

  mockItems: VehicleRentalItem[] = [
    {
      id: 1,
      vehicleId: 1,
      plateNumber: 'ABC-1234',
      brand: 'Toyota',
      model: 'Corolla',
      startMileage: 45000,
      endMileage: undefined,
      conditionAtStart: 'Veículo em bom estado, sem avarias visíveis',
      status: 'ATIVO'
    }
  ];

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private location: Location,
    private vehicleRentalService: VehicleRentalService
  ) {}

  ngOnInit(): void {
    this.route.params.pipe(takeUntil(this.destroy$)).subscribe(params => {
      if (params['id']) {
        this.rentalId = +params['id'];
        this.loadRentalDetails();
      }
    });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  private loadRentalDetails(): void {
    // Simulação de carregamento
    setTimeout(() => {
      this.vehicleRental = this.mockRental;
      this.rentalItems = this.mockItems;
      this.isLoading = false;
    }, 1000);
  }

  // Histórico
  loadHistory(): void {
    if (!this.rentalId) return;

    this.isLoadingHistory = true;

    // Chamada real para a API
    this.vehicleRentalService.getVehicleRentalHistoryByRental(this.rentalId).subscribe({
      next: (history: any) => {
        this.history = history;
        this.isLoadingHistory = false;
      },
      error: (error: any) => {
        this.error = 'Erro ao carregar histórico';
        this.isLoadingHistory = false;
        console.error('Erro ao carregar histórico:', error);
        // Em caso de erro, deixa array vazio
        this.history = [];
      }
    });
  }

  // Anexos
  loadAttachments(): void {
    if (!this.rentalId) return;

    this.isLoadingAttachments = true;

    // Chamada real para a API
    this.vehicleRentalService.getVehicleRentalAttachments(this.rentalId).subscribe({
      next: (attachments: any) => {
        this.attachments = attachments;
        this.isLoadingAttachments = false;
      },
      error: (error: any) => {
        this.error = 'Erro ao carregar anexos';
        this.isLoadingAttachments = false;
        console.error('Erro ao carregar anexos:', error);
        // Em caso de erro, deixa array vazio
        this.attachments = [];
      }
    });
  }

  // Navegação
  goBack(): void {
    this.router.navigate(['/commercial/vehicle-rentals']);
  }

  editRental(): void {
    if (this.rentalId) {
      this.router.navigate(['/commercial/vehicle-rentals', this.rentalId, 'edit']);
    }
  }

  goToApproval(): void {
    if (this.rentalId) {
      this.router.navigate(['/commercial/vehicle-rentals', this.rentalId, 'approve']);
    }
  }

  // Ações da locação
  startRental(): void {
    if (!this.vehicleRental) return;

    if (confirm(`Deseja iniciar a locação ${this.vehicleRental.rentalNumber}?`)) {
      // Simular início da locação
      alert('Locação iniciada com sucesso!');
      this.loadRentalDetails();
    }
  }

  endRental(): void {
    if (!this.vehicleRental) return;

    if (confirm(`Deseja finalizar a locação ${this.vehicleRental.rentalNumber}?`)) {
      // Simular finalização
      alert('Locação finalizada com sucesso!');
      this.loadRentalDetails();
    }
  }

  cancelRental(): void {
    if (!this.vehicleRental) return;

    const reason = prompt('Motivo do cancelamento:');
    if (reason) {
      // Simular cancelamento
      alert('Locação cancelada com sucesso!');
      this.loadRentalDetails();
    }
  }

  createAgreement(): void {
    if (!this.vehicleRental) return;
    this.router.navigate(['/financial/agreements/new'], {
      queryParams: {
        vehicleRentalId: this.vehicleRental.id,
        customerId: this.vehicleRental.customerId
      }
    });
  }

  // Controle de abas
  setActiveTab(tab: 'overview' | 'items' | 'history' | 'attachments'): void {
    this.activeTab = tab;

    // Carregar dados quando a aba for selecionada
    if (tab === 'history' && this.history.length === 0) {
      this.loadHistory();
    }
    
    if (tab === 'attachments' && this.attachments.length === 0) {
      this.loadAttachments();
    }
  }

  // Métodos auxiliares para anexos
  getAttachmentIconClass(fileName: string): string {
    const extension = fileName.split('.').pop()?.toLowerCase();
    switch (extension) {
      case 'pdf': return 'fas fa-file-pdf text-danger';
      case 'doc':
      case 'docx': return 'fas fa-file-word text-primary';
      case 'xls':
      case 'xlsx': return 'fas fa-file-excel text-success';
      case 'jpg':
      case 'jpeg':
      case 'png':
      case 'gif': return 'fas fa-file-image text-info';
      case 'zip':
      case 'rar': return 'fas fa-file-archive text-warning';
      default: return 'fas fa-file text-secondary';
    }
  }

  formatFileSize(size: number): string {
    if (!size) return 'N/A';
    const units = ['B', 'KB', 'MB', 'GB'];
    let unitIndex = 0;
    let fileSize = size;
    
    while (fileSize >= 1024 && unitIndex < units.length - 1) {
      fileSize /= 1024;
      unitIndex++;
    }
    
    return `${fileSize.toFixed(1)} ${units[unitIndex]}`;
  }

  downloadAttachment(attachment: any): void {
    // TODO: Implementar download real
    alert(`Download: ${attachment.fileName}`);
  }

  deleteAttachment(attachment: any): void {
    if (confirm(`Deseja excluir o anexo "${attachment.fileName}"?`)) {
      // TODO: Implementar exclusão real
      alert(`Anexo ${attachment.fileName} excluído!`);
    }
  }

  // Verificações de estado
  canStartRental(): boolean {
    return this.vehicleRental?.status === 'PENDENTE';
  }

  canEndRental(): boolean {
    return this.vehicleRental?.status === 'ATIVA';
  }

  canCancelRental(): boolean {
    return this.vehicleRental?.status !== 'CANCELADA' && this.vehicleRental?.status !== 'FINALIZADA';
  }

  canEditRental(): boolean {
    return this.vehicleRental?.status === 'PENDENTE';
  }

  canApproveRental(): boolean {
    return this.vehicleRental?.status === 'PENDENTE';
  }

  // Formatação
  formatDate(dateString: string): string {
    if (!dateString) return '';
    const date = new Date(dateString);
    return date.toLocaleDateString('pt-BR');
  }

  formatCurrency(value: number): string {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL'
    }).format(value);
  }

  getHistoryIconClass(action: string): string {
    switch (action.toUpperCase()) {
      case 'CREATED': return 'fas fa-plus-circle text-success';
      case 'APPROVED': return 'fas fa-check-circle text-primary';
      case 'REJECTED': return 'fas fa-times-circle text-danger';
      case 'STARTED': return 'fas fa-play-circle text-info';
      case 'ENDED': return 'fas fa-stop-circle text-warning';
      case 'CANCELLED': return 'fas fa-ban text-secondary';
      default: return 'fas fa-info-circle text-muted';
    }
  }

  getHistoryBadgeClass(action: string): string {
    switch (action.toUpperCase()) {
      case 'CREATED': return 'badge bg-success';
      case 'APPROVED': return 'badge bg-primary';
      case 'REJECTED': return 'badge bg-danger';
      case 'STARTED': return 'badge bg-info';
      case 'ENDED': return 'badge bg-warning';
      case 'CANCELLED': return 'badge bg-secondary';
      default: return 'badge bg-light text-dark';
    }
  }

  getStatusClass(status: string): string {
    switch (status.toLowerCase()) {
      case 'pendente': return 'badge bg-warning';
      case 'ativa': return 'badge bg-success';
      case 'finalizada': return 'badge bg-info';
      case 'cancelada': return 'badge bg-danger';
      default: return 'badge bg-secondary';
    }
  }

  getPriorityClass(priority: string): string {
    switch (priority.toLowerCase()) {
      case 'baixa': return 'badge bg-secondary';
      case 'normal': return 'badge bg-primary';
      case 'alta': return 'badge bg-warning';
      case 'urgente': return 'badge bg-danger';
      default: return 'badge bg-secondary';
    }
  }
}
